import { Environment, useGLTF, useTexture } from '@react-three/drei'
import { useEffect,useMemo,useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { useFightStore } from './store'
import * as THREE from 'three'
import type { ArenaDef } from './content'
import { stageObjectDefsForArena } from './arenaStage'
import { AssetBoundary } from './AssetBoundary'
import { scenarioProfileForArena } from './generatedArenaScenarios'
import { runtimePublicPath } from './runtimePublicPath'

function configureTexture(texture:THREE.Texture,repeat:number,color=false){
 texture.wrapS=texture.wrapT=THREE.RepeatWrapping
 texture.repeat.set(repeat,repeat)
 texture.anisotropy=8
 texture.colorSpace=color?THREE.SRGBColorSpace:THREE.NoColorSpace
 texture.needsUpdate=true
}

export function PolyHavenEnvironment({arena,onReady}:{arena:ArenaDef;onReady?:()=>void}){
 const cfg=scenarioProfileForArena(arena.id).pbr,base=runtimePublicPath(cfg.base)
 useEffect(()=>{onReady?.()},[arena.id,onReady])
 return <Environment files={`${base}/environment.hdr`} background backgroundIntensity={cfg.backgroundIntensity} environmentIntensity={cfg.environmentIntensity} blur={cfg.blur}/>
}
export function PolyHavenEnergyFloor({arena}:{arena:ArenaDef}){
 const cfg=scenarioProfileForArena(arena.id).pbr,base=runtimePublicPath(cfg.base)
 const [diffuse,normal,roughness]=useTexture([
  `${base}/ground_diff.jpg`,
  `${base}/ground_nor.jpg`,
  `${base}/ground_rough.jpg`,
 ])
 useMemo(()=>{
  configureTexture(diffuse,cfg.repeat,true)
  configureTexture(normal,cfg.repeat)
  configureTexture(roughness,cfg.repeat)
 },[diffuse,normal,roughness,cfg.repeat])
 return <group position={[0,.006,0]}>
  <mesh receiveShadow rotation={[-Math.PI/2,0,0]}>
   <circleGeometry args={[10.8,96]}/>
   <meshStandardMaterial map={diffuse} normalMap={normal} roughnessMap={roughness} color="#ffffff" roughness={1} metalness={.12} envMapIntensity={1.35}/>
  </mesh>
  {[3.4,6.3,9.4].map((r,i)=><mesh key={r} rotation={[-Math.PI/2,0,0]} position={[0,.012+i*.001,0]}>
   <ringGeometry args={[r-.025,r+.025,64]}/>
   <meshBasicMaterial color={arena.accent} transparent opacity={i===0?.3:.14} blending={THREE.AdditiveBlending}/>
  </mesh>)}
 </group>
}
function prepareArchitecture(scene:THREE.Object3D,targetHeight:number){
 const clone=scene.clone(true)
 clone.traverse(node=>{
  if(!(node instanceof THREE.Mesh))return
  node.castShadow=true
  node.receiveShadow=true
  if(node.material instanceof THREE.MeshStandardMaterial){
   const material=node.material.clone()
   material.roughness=Math.max(.28,Math.min(.78,material.roughness))
   material.metalness=Math.min(.34,material.metalness)
   material.envMapIntensity=Math.max(1.2,material.envMapIntensity)
   node.material=material
  }
 })
 const box=new THREE.Box3().setFromObject(clone)
 const size=box.getSize(new THREE.Vector3())
 const scale=size.y>0?targetHeight/size.y:1
 clone.scale.setScalar(scale)
 const grounded=new THREE.Box3().setFromObject(clone)
 clone.position.y-=grounded.min.y
 return clone
}

function ArchitecturePiece({url,position,rotationY,height,damage,collapse,direction}:{url:string;position:[number,number,number];rotationY:number;height:number;damage:number;collapse:boolean;direction:number}){
 const{scene}=useGLTF(url),root=useRef<THREE.Group>(null)
 const model=useMemo(()=>prepareArchitecture(scene,height),[scene,height])
 useFrame((_,dt)=>{if(!root.current)return;const cracked=Math.max(0,(damage-.32)/.68),tilt=(collapse?.82:.18)*cracked*direction,drop=collapse?height*.28*Math.min(1,damage+.2):height*.035*cracked;root.current.rotation.z=THREE.MathUtils.damp(root.current.rotation.z,tilt,3.6,dt);root.current.rotation.x=THREE.MathUtils.damp(root.current.rotation.x,collapse?.32*direction:0,3.2,dt);root.current.position.y=THREE.MathUtils.damp(root.current.position.y,-drop,3.8,dt)})
 return <group ref={root} position={position} rotation={[0,rotationY,0]}><primitive object={model}/></group>
}

function ArchitectureRubble({damage,collapse}:{damage:number;collapse:boolean}){
 const{scene}=useGLTF(runtimePublicPath('/polyhaven/models/stone_01.glb'))
 const pieces=useMemo(()=>Array.from({length:collapse?10:damage>.55?4:0},(_,i)=>{const c=scene.clone(true);prepareArchitecture(c,.28+(i%3)*.1);return c}),[scene,collapse,damage>.55])
 return <group>{pieces.map((piece,i)=>{const a=i*2.399,x=Math.cos(a)*(3.8+(i%4)*1.2),z=-8-Math.sin(a)*(1.5+(i%3));return <primitive key={i} object={piece} position={[x,0,z]} rotation={[i*.17,a,i*.11]} scale={.8+(i%3)*.25}/>})}</group>
}

export function EcosystemArchitecture({arena}:{arena:ArenaDef}){
 const p=scenarioProfileForArena(arena.id).architecture,objects=useFightStore(s=>s.stageObjects),defs=stageObjectDefsForArena(arena.id).filter(def=>!def.visualKind),states=defs.map(def=>objects.find(object=>object.id===def.id)),damage=states.map(state=>state?1-state.integrity/Math.max(1,state.maxIntegrity):0),collapsed=states.map(state=>state?.state==='collapsed'),maxDamage=Math.max(0,...damage),anyCollapsed=collapsed.some(Boolean)
 return <group>
  {p.walls.map(([x,r,z],i)=><AssetBoundary key={'wall-'+i} label={'arena wall '+i}><ArchitecturePiece url={runtimePublicPath("/ecosystem-assets/architecture/elyon-wall.glb")} position={[x,0,z]} rotationY={r} height={4.8} damage={damage[i]??maxDamage} collapse={collapsed[i]??anyCollapsed} direction={i===0?-1:1}/></AssetBoundary>)}
  <AssetBoundary label="arena gateway"><ArchitecturePiece url={runtimePublicPath("/ecosystem-assets/architecture/elyon-gateway.glb")} position={[p.gateway[0],0,p.gateway[2]]} rotationY={p.gateway[1]} height={6.8} damage={damage[2]??maxDamage} collapse={collapsed[2]??anyCollapsed} direction={1}/></AssetBoundary>
  <AssetBoundary label="architecture rubble"><ArchitectureRubble damage={maxDamage} collapse={anyCollapsed}/></AssetBoundary>
 </group>
}
